import { useCallback, useState, useMemo } from 'react';
import type { ActionMeta, MultiValue } from 'react-select';
import Select from 'react-select';
import { useQuery } from '@tanstack/react-query';
import type { BrandInsightsInstanceContext, ProjectFilter } from '@trinity/chat';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import * as Presentation from './Presentation';

export type Props = {
  context: BrandInsightsInstanceContext.ItemWithMetadata;
  value: ProjectFilter.Item[];
  onChange: (value: ProjectFilter.Item[], meta: ActionMeta<ProjectFilter.Item>) => unknown;
  disabled?: boolean;
};

export function Project({ disabled, context, value, onChange }: Props) {
  const theme = useTheme();

  const selectStyles = useMemo(() => Presentation.buildSelectStyles(theme), [theme]);
  const selectTheme = useMemo(() => Presentation.buildSelectTheme(theme), [theme]);

  const selected = useMemo(() => value, [value]);

  const handleChange = useCallback((value: MultiValue<ProjectFilter.Item>, meta: ActionMeta<ProjectFilter.Item>) => {
    onChange(Array.from(value), meta);
  }, [onChange]);

  const [inputValue, setInputValue] = useState('');
  const [emptyDisabled, setEmptyDisabled] = useState(false);

  const apiClient = useChatApiClient();
  const optionsQuery = useQuery({
    queryKey: [`get:brand-insights/chat/filters/projects`, context, inputValue],
    queryFn: async () => {
      const result = await apiClient.chat.searchChatProjects({
        context,
        query: inputValue,
      });

      setEmptyDisabled(result.empty);

      return result.results;
    },
    placeholderData: [],
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 5000,
    enabled: !disabled,
  });

  const handleInputChange = useCallback((newValue: string) => setInputValue(newValue), []);

  const handleNoOptionsMessage = useCallback((obj: { inputValue: string }) => {
    if (optionsQuery.isFetching) return `Loading...`;
    return obj.inputValue?.length ? `No projects found` : null;
  }, [optionsQuery.isFetching]);

  return (
    <Presentation.Row>
      <Presentation.Label>Project</Presentation.Label>
      <Presentation.Value>
        <Select<ProjectFilter.Item, true>
          styles={selectStyles}
          components={{
            DropdownIndicator: null,
          }}
          theme={selectTheme}
          value={selected}
          isMulti
          options={optionsQuery.data}
          placeholder={`Search projects...`}
          noOptionsMessage={handleNoOptionsMessage}
          getOptionLabel={o => o.name}
          getOptionValue={o => `${o.id}`}
          onChange={handleChange}
          onInputChange={handleInputChange}
          isDisabled={disabled || emptyDisabled}
          menuPlacement="auto" />
      </Presentation.Value>
    </Presentation.Row>
  );
}